import { Component } from '@theme/component';

/** Responsive cards, native touch/trackpad scroll, arrow keys and merchant block selection. */
class CorporateCarousel extends Component {
  #abort;
  #resize;
  #frame;

  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#connect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #cleanup() {
    this.#abort?.abort();
    this.#resize?.disconnect();
    cancelAnimationFrame(this.#frame);
    for (const bubble of this.querySelectorAll('.ic-ruta-detalle[popover]')) {
      if (bubble.matches(':popover-open')) bubble.hidePopover();
      bubble.removeAttribute('popover');
      const close = bubble.querySelector('.ic-ruta-cerrar');
      if (close) close.hidden = true;
    }
  }

  #connect() {
    this.#cleanup();
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#update());
    }, { passive: true, signal });
    track.addEventListener('keydown', (event) => {
      if (event.target !== track) return;
      const rtl = getComputedStyle(track).direction === 'rtl';
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#move((event.key === 'ArrowRight') !== rtl ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.#go(event.key === 'Home' ? 0 : this.#cards().length - 1);
      }
    }, { signal });
    this.#connectBubbles(track, signal);
    track.addEventListener('toggle', (event) => {
      if (event.target instanceof HTMLDetailsElement) this.#toggleStep(event.target);
      this.#layout();
    }, { capture: true, signal });
    document.addEventListener('shopify:block:select', (event) => {
      if (!(event.target instanceof Element) || !track.contains(event.target)) return;
      const cards = this.#cards();
      const index = cards.findIndex((card) => card.contains(event.target));
      if (index >= 0) {
        const detail = cards[index].querySelector('.ic-ruta-tarjeta');
        if (detail instanceof HTMLDetailsElement) detail.open = true;
        this.#go(index, false);
      }
    }, { signal });
    if ('ResizeObserver' in window) {
      this.#resize = new ResizeObserver(() => this.#layout());
      this.#resize.observe(track);
      const diagram = track.querySelector('.ic-ruta-diagrama');
      if (diagram) this.#resize.observe(diagram);
    } else {
      window.addEventListener('resize', () => this.#layout(), { signal });
    }
    this.#layout();
    if (this.refs.controls instanceof HTMLElement) this.refs.controls.hidden = false;
  }

  #connectBubbles(track, signal) {
    if (!('showPopover' in HTMLElement.prototype)) return;
    for (const bubble of track.querySelectorAll('.ic-ruta-detalle')) {
      bubble.setAttribute('popover', 'auto');
      const close = bubble.querySelector('.ic-ruta-cerrar');
      if (close) close.hidden = false;
      bubble.addEventListener('toggle', (event) => {
        if (event.newState !== 'closed') return;
        const card = bubble.closest('details');
        if (card) card.open = false;
      }, { signal });
    }
    window.addEventListener('scroll', () => this.#positionBubble(), { capture: true, passive: true, signal });
    window.addEventListener('resize', () => this.#positionBubble(), { signal });
    const initial = track.querySelector('.ic-ruta-tarjeta[open]');
    if (initial) this.#toggleStep(initial);
  }

  #toggleStep(card) {
    const bubble = card.querySelector('.ic-ruta-detalle[popover]');
    if (!bubble) return;
    if (card.open) {
      for (const other of this.querySelectorAll('.ic-ruta-tarjeta[open]')) {
        if (other !== card) other.open = false;
      }
      if (!bubble.matches(':popover-open')) bubble.showPopover({ source: card.querySelector('summary') });
      this.#positionBubble();
    } else if (bubble.matches(':popover-open')) {
      bubble.hidePopover();
    }
  }

  #positionBubble() {
    const bubble = this.querySelector('.ic-ruta-detalle[popover]:popover-open');
    if (!bubble) return;
    const summary = bubble.closest('details')?.querySelector('summary');
    if (!summary) return;
    const anchor = summary.getBoundingClientRect();
    const rect = bubble.getBoundingClientRect();
    const gap = 14, margin = 16;
    const left = Math.max(margin, Math.min(innerWidth - rect.width - margin, anchor.left + anchor.width / 2 - rect.width / 2));
    const above = anchor.bottom + gap + rect.height > innerHeight - margin && anchor.top - gap - rect.height >= margin;
    const top = Math.max(margin, Math.min(innerHeight - rect.height - margin, above ? anchor.top - rect.height - gap : anchor.bottom + gap));
    bubble.style.left = `${left}px`;
    bubble.style.top = `${top}px`;
    bubble.dataset.side = above ? 'above' : 'below';
    const tail = Math.max(22, Math.min(rect.width - 36, anchor.left + anchor.width / 2 - left - 7));
    const rtl = getComputedStyle(bubble).direction === 'rtl';
    bubble.style.setProperty('--ic-bubble-tail', `${rtl ? rect.width - tail - 14 : tail}px`);
  }

  /** Close a roadmap speech bubble and return keyboard focus to its card. */
  closeStep(event) {
    const card = event.target instanceof Element ? event.target.closest('.ic-ruta-tarjeta') : null;
    if (!(card instanceof HTMLDetailsElement)) return;
    card.open = false;
    this.#toggleStep(card);
    card.querySelector('summary')?.focus({ preventScroll: true });
  }

  #layout() {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;
    if (this.hasAttribute('data-min-card')) {
      const minimum = Math.max(240, Number(this.dataset.minCard) || 300);
      const gap = parseFloat(getComputedStyle(track).columnGap) || 24;
      const count = Math.max(1, Math.min(track.children.length, Math.floor((track.clientWidth + gap) / (minimum + gap))));
      track.style.setProperty('--ic-cards-visible', String(count));
      track.dataset.single = String(count === 1);
    }
    this.#update();
    this.#drawFeedback();
    this.#positionBubble();
  }

  #cards() {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return [];
    const stages = track.querySelectorAll('[data-carousel-item]');
    return stages.length ? Array.from(stages) : Array.from(track.children);
  }

  /** Anchor feedback to the actual cards, including after native disclosures resize. */
  #drawFeedback() {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;
    const diagram = track.querySelector('.ic-ruta-diagrama');
    const svg = diagram?.querySelector('.ic-ruta-retorno svg');
    if (!diagram || !svg || getComputedStyle(svg.parentElement).display === 'none') return;
    const source = diagram.querySelector('[data-step="parte_06"] .ic-ruta-tarjeta');
    const metrics = diagram.querySelector('[data-step="parte_06"] .ic-ruta-paralelo');
    const target = diagram.querySelector('.ic-ruta-paralelo--compartido');
    const implementation = diagram.querySelector('[data-step="parte_03"] .ic-ruta-tarjeta');
    const outer = svg.querySelector('[data-feedback-path="outer"]');
    const inner = svg.querySelector('[data-feedback-path="inner"]');
    const upward = svg.querySelector('[data-feedback-path="implementation"]');
    for (const path of [outer, inner, upward]) path?.removeAttribute('d');
    if (!source || !target) return;
    const bounds = diagram.getBoundingClientRect();
    const rect = (element) => {
      const r = element.getBoundingClientRect();
      return { left: r.left - bounds.left, right: r.right - bounds.left, top: r.top - bounds.top, bottom: r.bottom - bounds.top, x: r.left - bounds.left + r.width / 2, y: r.top - bounds.top + r.height / 2 };
    };
    const s = rect(source);
    const t = rect(target);
    const rtl = getComputedStyle(track).direction === 'rtl';
    const sign = rtl ? -1 : 1;
    const edge = rtl ? s.left : s.right;
    const rail = rtl ? 8 : bounds.width - 8;
    const bottom = bounds.height - 16;
    svg.setAttribute('viewBox', `0 0 ${bounds.width} ${bounds.height}`);
    outer?.setAttribute('d', `M ${edge} ${s.y} C ${rail} ${s.y}, ${rail} ${bottom}, ${s.x} ${bottom} C ${t.x} ${bottom}, ${t.x} ${t.bottom + 40}, ${t.x} ${t.bottom + 3}`);
    if (metrics) {
      const m = rect(metrics);
      const end = rtl ? t.left + 28 : t.right - 28;
      const low = Math.max(m.bottom, t.bottom) + 62;
      inner?.setAttribute('d', `M ${m.x} ${m.bottom} C ${m.x} ${low}, ${end + sign * 40} ${low}, ${end} ${t.bottom + 3}`);
    }
    if (implementation) {
      const i = rect(implementation);
      upward?.setAttribute('d', `M ${i.x} ${t.top} L ${i.x} ${i.bottom + 3}`);
    }
  }

  #current() {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return 0;
    const rtl = getComputedStyle(track).direction === 'rtl';
    const bounds = track.getBoundingClientRect();
    let current = 0;
    let nearest = Infinity;
    this.#cards().forEach((card, index) => {
      const rect = card.getBoundingClientRect();
      const distance = Math.abs(rtl ? rect.right - bounds.right : rect.left - bounds.left);
      if (distance < nearest) { current = index; nearest = distance; }
    });
    return current;
  }

  #update() {
    const { track, previous, next, status } = this.refs;
    const cards = this.#cards();
    if (!(track instanceof HTMLElement) || !cards.length) return;
    const rtl = getComputedStyle(track).direction === 'rtl';
    const bounds = track.getBoundingClientRect();
    const first = cards[0].getBoundingClientRect();
    const last = cards.at(-1).getBoundingClientRect();
    this.dataset.overflow = String(track.scrollWidth > track.clientWidth + 2);
    if (previous instanceof HTMLButtonElement) previous.disabled = rtl ? first.right <= bounds.right + 2 : first.left >= bounds.left - 2;
    if (next instanceof HTMLButtonElement) next.disabled = rtl ? last.left >= bounds.left - 2 : last.right <= bounds.right + 2;
    if (status instanceof HTMLElement) {
      const text = `${this.#current() + 1} / ${cards.length}`;
      if (status.textContent !== text) status.textContent = text;
    }
  }

  #go(index, smooth = true) {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;
    const cards = this.#cards();
    const targetIndex = Math.max(0, Math.min(index, cards.length - 1));
    const card = cards[targetIndex];
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const bounds = track.getBoundingClientRect();
    const rtl = getComputedStyle(track).direction === 'rtl';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (track.classList.contains('ic-ruta-viewport') && (targetIndex === 0 || targetIndex === cards.length - 1)) {
      track.scrollTo({ left: targetIndex === 0 ? 0 : (rtl ? -1 : 1) * (track.scrollWidth - track.clientWidth), behavior: smooth && !reduced ? 'smooth' : 'instant' });
      return;
    }
    track.scrollBy({ left: rtl ? rect.right - bounds.right : rect.left - bounds.left, behavior: smooth && !reduced ? 'smooth' : 'instant' });
  }

  #move(delta) { this.#go(this.#current() + delta); }

  /** Navigate one card while preserving focus on the button. */
  previous() { this.#move(-1); }
  /** Navigate one card while preserving focus on the button. */
  next() { this.#move(1); }
}

if (!customElements.get('ic-corporate-carousel-component')) {
  customElements.define('ic-corporate-carousel-component', CorporateCarousel);
}
